/**
 * MapboxDraw style layers for the AOI polygon: transparent fill, active stroke,
 * vertices, midpoints, and the in-progress drawing line.
 *
 * Lifted verbatim from `pages/map-query/map-section/draw-polygon-control.tsx` so
 * the future QueryMap AOI control and the existing draw control converge on one
 * look. Not consumed in P1 — the AOI control ships with the phase that enables
 * `drawAoi`; this lives here to keep `components/query-map/` colocated with its
 * planned dependencies.
 */
/**
 * The colour MapboxDraw outlines and vertices are drawn in, per control. Exported
 * so a host filling the shape itself can match rather than hard-coding them.
 *
 * An area under construction is blue: its own Display Color describes the saved
 * area, not the thing being drawn (UAT 2).
 */
export const DRAW_COLOR = '#ff0000';
export const POLYGON_DRAW_COLOR = '#0F62FE';

const drawStyles = (color: string) => [
  // Polygon fill — transparent
  {
    id: 'gl-draw-polygon-fill',
    type: 'fill' as const,
    filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
    paint: { 'fill-color': color, 'fill-opacity': 0 },
  },
  // Polygon outline — thick red
  {
    id: 'gl-draw-polygon-stroke-active',
    type: 'line' as const,
    filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
    paint: { 'line-color': color, 'line-width': 3 },
  },
  // Vertex points
  {
    id: 'gl-draw-point',
    type: 'circle' as const,
    filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'vertex']],
    paint: { 'circle-radius': 5, 'circle-color': color },
  },
  // Midpoints
  {
    id: 'gl-draw-point-mid',
    type: 'circle' as const,
    filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'midpoint']],
    paint: { 'circle-radius': 3, 'circle-color': color },
  },
  // Line while drawing
  {
    id: 'gl-draw-line',
    type: 'line' as const,
    filter: ['all', ['==', '$type', 'LineString'], ['!=', 'mode', 'static']],
    paint: { 'line-color': color, 'line-width': 3 },
  },
];

/** The single-AOI control's look, unchanged: red. */
export const AOI_DRAW_STYLES = drawStyles(DRAW_COLOR);

/** The multi-polygon control's look — blue, for an area being constructed. */
export const POLYGON_DRAW_STYLES = drawStyles(POLYGON_DRAW_COLOR);
